import { memo, useMemo } from "react";
import { DateTime } from "luxon";
import { Line } from "react-chartjs-2";
import { ISlaTrendItem } from "../../../interfaces/dashboard";
import { LOCALE } from "../../../utils/constants";
import ChartContainer from "../ChartContainer";
import "../chartSetup";

// Etiqueta de eje X compacta: día y mes ("15/6"). El año se alude únicamente
// cuando difiere del actual (ej. en ene-2026, los puntos de dic-2025 se ven como
// "31/12/25"), evitando la aglomeración de la fecha ISO completa.
const formatTrendLabel = (iso: string, currentYear: number): string => {
  const dt = DateTime.fromISO(iso).setLocale(LOCALE);
  if (!dt.isValid) return iso;
  return dt.year === currentYear ? dt.toFormat("d/M") : dt.toFormat("d/M/yy");
};

// Cambia el cursor sobre la leyenda para señalar que es interactiva (B3b): al
// hacer clic en una referencia se muestra/oculta esa serie.
const setLegendCursor =
  (cursor: string) =>
    (event: { native: Event | null }) => {
      const target = event.native?.target as HTMLElement | null;
      if (target) target.style.cursor = cursor;
    };

const OPTIONS = {
  responsive: true,
  maintainAspectRatio: false,
  plugins: {
    legend: {
      position: "bottom" as const,
      // Referencias con círculo en lugar de rectángulo (B3a).
      labels: {
        usePointStyle: true,
        pointStyle: "circle" as const,
        boxWidth: 8,
        boxHeight: 8,
      },
      onHover: setLegendCursor("pointer"),
      onLeave: setLegendCursor("default"),
    },
  },
  scales: {
    y: { beginAtZero: true },
  },
};

interface ISlaTrendProps {
  data: ISlaTrendItem[];
}

const SlaTrend = ({ data }: ISlaTrendProps) => {
  const chartData = useMemo(() => {
    const currentYear = DateTime.now().year;
    return {
      labels: data.map((d) => formatTrendLabel(d.date, currentYear)),
      datasets: [
        {
          label: "A tiempo",
          data: data.map((d) => d.onTime),
          borderColor: "#2e7d32",
          backgroundColor: "rgba(46,125,50,0.1)",
          tension: 0.3,
        },
        {
          label: "Alerta inicial",
          data: data.map((d) => d.earlyDelay),
          borderColor: "#f57c00",
          backgroundColor: "rgba(245,124,0,0.1)",
          tension: 0.3,
        },
        {
          label: "Alerta intermedia",
          data: data.map((d) => d.intermediateAlert),
          borderColor: "#c62828",
          backgroundColor: "rgba(198,40,40,0.1)",
          tension: 0.3,
        },
        {
          label: "Demora final",
          data: data.map((d) => d.finalDelay),
          borderColor: "#6a1b9a",
          backgroundColor: "rgba(106,27,154,0.1)",
          tension: 0.3,
        },
      ],
    };
  }, [data]);

  return (
    <ChartContainer>
      <Line data={chartData} options={OPTIONS} />
    </ChartContainer>
  );
};

export default memo(SlaTrend);
